import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";

/**
 * Gloss scheduler / sync-all: accounts that must not run through automatic sync.
 *
 * Org-owned accounts are excluded unconditionally, regardless of the user's write access to the
 * organization. Write access answers "is this user allowed to write to the org", not "will the
 * sync machinery actually write there correctly" — and the latter is false today for everyone:
 * the `saveToVault` call sites don't thread `organizationId`/`collectionIds` through for
 * newly-created records, so even a legitimate write-access holder's auto-sync would silently
 * write new transactions/categories into their own personal vault instead of the org's.
 *
 * There is no sync path for org-owned accounts today. Account details disables Fetch data for
 * them and says so (#4458, #5791).
 *
 * Data-plugin accounts (Opal Card, Australian Super, etc.) are excluded too: they sync through
 * an interactive credential dialog (see account-details' "Fetch data" button), and
 * GlossSyncFactory has no headless syncer for Origin.plugin — routing one through here throws
 * and marks it (and any other plugin accounts in the same batch) as unknownError.
 */
export function isExcludedFromAutoSync(account: AccountView): boolean {
  if (account.isOrganizationOwned?.()) {
    return true;
  }
  return (
    !account.origin ||
    account.origin === Origin.manual ||
    account.origin === Origin.system ||
    // GlossSyncFactory only knows how to sync Basiq/Plaid/Fiskil origins — a plugin-created
    // account (Opal Card, Australian Super, ...) routed in here throws, and gets stamped with a
    // spurious "unknownError" sync status despite its own plugin fetch having succeeded. Plugin
    // accounts refresh through their own plugin flow instead (see plugin-config.component.ts).
    account.origin === Origin.plugin
  );
}
